import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import TimerIcon from '@mui/icons-material/Timer';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

interface MsalErrorDecoderProps {
  onBack: () => void;
}

interface ErrorDefinition {
  code: string;
  category: 'authentication' | 'configuration' | 'network' | 'consent' | 'token' | 'server';
  severity: 'error' | 'warning' | 'info';
  description: string;
  commonCauses: string[];
  solutions: string[];
  relatedCodes?: string[];
  docsUrl?: string;
  codeExample?: string;
  whoFixes: 'iam' | 'developer' | 'both' | 'user';
  iamActions?: string[];
  developerActions?: string[];
  estimatedFixTime?: string;
}

// Comprehensive MSAL error definitions
const MSAL_ERRORS: Record<string, ErrorDefinition> = {
  'invalid_grant': {
    code: 'invalid_grant',
    category: 'authentication',
    severity: 'error',
    description: 'The provided authorization grant (authorization code, refresh token, etc.) is invalid, expired, revoked, or does not match the redirect URI used in the authorization request.',
    commonCauses: [
      'Refresh token has expired (24 hours for SPAs, up to 90 days for confidential clients)',
      'User password has changed',
      'Conditional Access policy requires reauthentication',
      'User account has been disabled or deleted',
      'Session has been revoked by administrator',
    ],
    solutions: [
      'Prompt the user to sign in again interactively',
      'Clear the token cache and reinitialize authentication',
      'Check if user account is active in Azure AD',
      'Verify Conditional Access policies are not blocking token refresh',
      'Implement proper error handling to trigger interactive login',
    ],
    relatedCodes: ['AADSTS50173', 'AADSTS700082', 'AADSTS700084'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes#aadsts-error-codes',
    whoFixes: 'developer',
    developerActions: [
      'Implement proper error handling for InteractionRequiredAuthError',
      'Add fallback to interactive login (loginPopup/loginRedirect)',
      'Clear MSAL cache and reinitialize authentication flow',
    ],
    estimatedFixTime: '15-30 minutes',
  },
  'interaction_required': {
    code: 'interaction_required',
    category: 'authentication',
    severity: 'warning',
    description: 'Silent token acquisition failed. User interaction is required to complete authentication (e.g., MFA, consent, or reauthentication).',
    commonCauses: [
      'Multi-factor authentication (MFA) is required',
      'User consent is needed for additional scopes',
      'Conditional Access policy requires user interaction',
      'Session has expired and requires fresh authentication',
      'Password has expired and needs to be changed',
    ],
    solutions: [
      'Call acquireTokenPopup() or acquireTokenRedirect() to prompt user',
      'Handle InteractionRequiredAuthError in your catch block',
      'Implement fallback to interactive authentication',
      'Check Conditional Access policies and adjust flow accordingly',
      'Ensure proper scopes are requested during initial login',
    ],
    relatedCodes: ['consent_required', 'login_required'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-error-handling-js',
    codeExample: `try {
  const response = await msalInstance.acquireTokenSilent(request);
} catch (error) {
  if (error instanceof InteractionRequiredAuthError) {
    // Fallback to interactive
    await msalInstance.acquireTokenPopup(request);
  }
}`,
    whoFixes: 'developer',
    developerActions: [
      'Implement error handling for InteractionRequiredAuthError',
      'Add fallback from acquireTokenSilent to acquireTokenPopup/Redirect',
      'This is expected behavior - not a bug to fix, but proper flow to implement',
    ],
    estimatedFixTime: '10-15 minutes',
  },
  'consent_required': {
    code: 'consent_required',
    category: 'consent',
    severity: 'warning',
    description: 'User or admin consent is required for the requested permissions/scopes.',
    commonCauses: [
      'Application requesting new scopes not previously consented',
      'Admin consent required but not granted',
      'Dynamic consent needed for incremental permissions',
      'Consent has been revoked by user or admin',
    ],
    solutions: [
      'Call acquireTokenPopup() or acquireTokenRedirect() to prompt consent',
      'Request admin to grant tenant-wide consent in Azure portal',
      'Use incremental consent pattern for additional scopes',
      'Check app registration for required vs optional permissions',
      'Verify application permissions in Enterprise Applications blade',
    ],
    relatedCodes: ['AADSTS65001', 'AADSTS65004', 'AADSTS65005'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/consent-framework',
    whoFixes: 'both',
    iamActions: [
      'Navigate to Azure Portal → Enterprise Applications → [Your App] → Permissions',
      'Click "Grant admin consent for [tenant]" button',
      'Verify all requested permissions are shown and click Accept',
    ],
    developerActions: [
      'Implement consent prompt using acquireTokenPopup with forceConsent flag',
      'Handle consent_required error and trigger interactive flow',
      'Consider using incremental consent for better UX',
    ],
    estimatedFixTime: '2-5 minutes (IAM) / 10-15 minutes (Dev)',
  },
  'login_required': {
    code: 'login_required',
    category: 'authentication',
    severity: 'warning',
    description: 'No valid authentication session exists. User needs to sign in.',
    commonCauses: [
      'User session has expired',
      'No cached account found',
      'User has signed out from another application',
      'Browser cookies/cache cleared',
    ],
    solutions: [
      'Redirect user to interactive login',
      'Call loginPopup() or loginRedirect()',
      'Check if user account exists in cache before silent requests',
      'Implement proper session management',
    ],
    relatedCodes: ['AADSTS50058'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications',
    whoFixes: 'developer',
    developerActions: [
      'Check for active account before calling acquireTokenSilent',
      'Implement proper fallback to loginPopup/loginRedirect',
      'Handle login_required error in catch block',
    ],
    estimatedFixTime: '10-15 minutes',
  },
  'AADSTS50058': {
    code: 'AADSTS50058',
    category: 'authentication',
    severity: 'error',
    description: 'A silent sign-in request was sent but no user is signed in.',
    commonCauses: [
      'Attempting silent authentication without active session',
      'Session cookies not present or expired',
      'User signed out in another tab',
    ],
    solutions: [
      'Use interactive login methods (loginPopup/loginRedirect)',
      'Check for active accounts before calling acquireTokenSilent',
      'Implement proper account selection logic',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes',
    whoFixes: 'developer',
    developerActions: [
      'Add check: if (!account) { await loginPopup(); }',
      'Never call acquireTokenSilent without an active account',
      'Implement proper account state management',
    ],
    estimatedFixTime: '10 minutes',
  },
  'AADSTS50076': {
    code: 'AADSTS50076',
    category: 'authentication',
    severity: 'warning',
    description: 'Due to a configuration change or security policy, you must use multi-factor authentication to access the resource.',
    commonCauses: [
      'Conditional Access policy requires MFA',
      'Per-user MFA is enabled',
      'Security defaults are enforced',
    ],
    solutions: [
      'Prompt user for interactive authentication to complete MFA',
      'Ensure application can handle MFA challenges',
      'Review Conditional Access policies',
      'Use acquireTokenPopup/Redirect for MFA flow',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/authentication/concept-mfa-howitworks',
    whoFixes: 'both',
    iamActions: [
      'Verify Conditional Access policy requiring MFA is intended',
      'Check if user needs to be exempt from policy',
      'Ensure user has registered MFA methods',
    ],
    developerActions: [
      'Handle this error by prompting interactive authentication',
      'Allow user to complete MFA flow',
      'Do not treat as application error - this is expected behavior',
    ],
    estimatedFixTime: '5-10 minutes',
  },
  'AADSTS65001': {
    code: 'AADSTS65001',
    category: 'consent',
    severity: 'error',
    description: 'The user or administrator has not consented to use the application.',
    commonCauses: [
      'First-time user accessing the application',
      'New permissions added to app registration',
      'Consent was previously declined',
    ],
    solutions: [
      'Trigger consent flow using acquireTokenPopup/Redirect',
      'Admin can pre-consent in Azure portal (Enterprise Apps → Permissions)',
      'Use admin consent endpoint for tenant-wide consent',
      'Review and adjust requested permissions',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/v2-admin-consent',
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → Enterprise Applications → [Your App]',
      'Click "Permissions" in left menu',
      'Click "Grant admin consent for [tenant name]"',
      'Review permissions and click Accept',
    ],
    estimatedFixTime: '2-3 minutes',
  },
  'AADSTS700016': {
    code: 'AADSTS700016',
    category: 'configuration',
    severity: 'error',
    description: 'Application with identifier was not found in the directory.',
    commonCauses: [
      'Incorrect client ID (Application ID) in configuration',
      'Application not registered in the tenant',
      'Application deleted or not yet created',
      'Wrong tenant being targeted',
    ],
    solutions: [
      'Verify client ID matches app registration in Azure portal',
      'Check application exists in correct Azure AD tenant',
      'Ensure app registration is not soft-deleted',
      'Verify authority URL points to correct tenant',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/quickstart-register-app',
    whoFixes: 'both',
    iamActions: [
      'Verify app registration exists: Azure Portal → App Registrations → All applications',
      'Copy the correct Application (client) ID from Overview page',
      'Provide correct client ID to developer',
    ],
    developerActions: [
      'Update clientId in MSAL configuration to match Azure Portal',
      'Verify tenant ID in authority URL is correct',
      'Check for typos in client ID',
    ],
    estimatedFixTime: '5 minutes',
  },
  'AADSTS90002': {
    code: 'AADSTS90002',
    category: 'configuration',
    severity: 'error',
    description: 'Tenant does not exist or could not be found.',
    commonCauses: [
      'Incorrect tenant ID in authority URL',
      'Tenant has been deleted',
      'Using /common endpoint with single-tenant app',
    ],
    solutions: [
      'Verify tenant ID in authority configuration',
      'Use correct authority endpoint (common, organizations, consumers, or tenant-specific)',
      'Check tenant exists and is active',
      'For single-tenant apps, use tenant-specific authority',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-client-application-configuration',
    whoFixes: 'both',
    iamActions: [
      'Verify tenant ID from Azure Portal → Azure Active Directory → Overview',
      'Provide correct tenant ID to developer',
    ],
    developerActions: [
      'Update authority in MSAL config to use correct tenant ID',
      'Verify format: https://login.microsoftonline.com/{tenantId}',
    ],
    estimatedFixTime: '5 minutes',
  },
  'AADSTS700082': {
    code: 'AADSTS700082',
    category: 'authentication',
    severity: 'error',
    description: 'The refresh token has expired due to inactivity.',
    commonCauses: [
      'Refresh token expired (24 hours for SPAs, up to 90 days for confidential clients)',
      'Refresh token max lifetime exceeded',
      'Token lifetime policies have changed',
    ],
    solutions: [
      'Prompt user to sign in again interactively',
      'Clear token cache and reinitialize',
      'Review token lifetime policies',
      'Implement proper session management',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/refresh-tokens',
    whoFixes: 'developer',
    developerActions: [
      'Catch this error and trigger interactive login',
      'Clear MSAL cache before re-authenticating',
      'This is expected after long inactivity - implement proper handling',
    ],
    estimatedFixTime: '10-15 minutes',
  },
  'AADSTS50105': {
    code: 'AADSTS50105',
    category: 'authentication',
    severity: 'error',
    description: 'The signed in user is not assigned to a role for the application.',
    commonCauses: [
      'User assignment required in app registration but user not assigned',
      'Application requires specific app roles',
      'Enterprise app has "User assignment required" enabled',
    ],
    solutions: [
      'Assign user to application in Enterprise Applications',
      'Disable "User assignment required" if appropriate',
      'Assign appropriate app roles to users/groups',
      'Check app role assignments in Azure portal',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/manage-apps/assign-user-or-group-access-portal',
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → Enterprise Applications → [Your App]',
      'Click "Users and groups" in left menu',
      'Click "Add user/group"',
      'Select users and assign appropriate roles',
      'Alternative: Turn off "User assignment required" in Properties blade if all users should have access',
    ],
    estimatedFixTime: '3-5 minutes',
  },
  'AADSTS50020': {
    code: 'AADSTS50020',
    category: 'authentication',
    severity: 'error',
    description: 'User account from identity provider does not exist in tenant.',
    commonCauses: [
      'External user (guest) not invited to tenant',
      'B2B invitation not accepted',
      'User deleted but trying to access with cached credentials',
    ],
    solutions: [
      'Invite external user to tenant via Azure AD B2B',
      'Ensure guest user has accepted invitation',
      'Verify user exists in the target tenant',
      'Check guest user settings in Azure AD',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/external-identities/what-is-b2b',
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → Azure AD → Users',
      'Click "New guest user"',
      'Enter email and send invitation',
      'Ensure guest user accepts invitation email',
    ],
    estimatedFixTime: '5-10 minutes',
  },
  'AADSTS50011': {
    code: 'AADSTS50011',
    category: 'configuration',
    severity: 'error',
    description: 'The redirect URI specified in the request does not match the redirect URIs configured for the application.',
    commonCauses: [
      'Redirect URI in code does not exactly match app registration',
      'Trailing slash mismatch (e.g., /callback vs /callback/)',
      'HTTP vs HTTPS mismatch',
      'Localhost port number different from registered',
      'Query parameters or fragments in redirect URI',
    ],
    solutions: [
      'Verify redirect URI exactly matches app registration (case-sensitive)',
      'Add all required redirect URIs in Azure portal (Authentication blade)',
      'Check for trailing slashes - they must match exactly',
      'Ensure protocol (http/https) matches registration',
      'For SPAs, use "Single-page application" platform type',
      'Remove query strings and fragments from redirect URI',
    ],
    relatedCodes: ['AADSTS50020'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/reply-url',
    whoFixes: 'iam',
    iamActions: [
      'Open Azure Portal → App Registrations → [Your App] → Authentication',
      'Look at "Redirect URIs" section',
      'Click "Add URI" and enter the EXACT URI from error message (including trailing slash if present)',
      'Click Save',
    ],
    estimatedFixTime: '2 minutes',
  },
  'AADSTS50013': {
    code: 'AADSTS50013',
    category: 'token',
    severity: 'error',
    description: 'Assertion failed signature validation. The token signature is invalid or the token has been tampered with.',
    commonCauses: [
      'Token has been modified or corrupted',
      'Clock skew between systems',
      'Wrong signing key or certificate used',
      'Token validation settings misconfigured',
      'Token issued for different audience',
    ],
    solutions: [
      'Do not modify tokens - treat as opaque strings',
      'Ensure system clocks are synchronized (use NTP)',
      'Verify token validation parameters (audience, issuer)',
      'Check certificate/key configuration in app registration',
      'Clear token cache and request new token',
      'Validate tokens using official MSAL libraries',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/access-tokens',
    whoFixes: 'developer',
    developerActions: [
      'Never modify or parse tokens manually - treat as opaque strings',
      'Check system clock synchronization',
      'Verify token validation library configuration',
      'Request fresh token if validation fails',
    ],
    estimatedFixTime: '20-30 minutes',
  },
  'AADSTS65004': {
    code: 'AADSTS65004',
    category: 'consent',
    severity: 'warning',
    description: 'User declined to consent to access the app.',
    commonCauses: [
      'User clicked "Cancel" on consent screen',
      'User does not trust the application',
      'Permissions requested seem excessive',
      'User confused about what they are consenting to',
    ],
    solutions: [
      'Provide clear explanation of why permissions are needed',
      'Request only necessary permissions (principle of least privilege)',
      'Use incremental consent - request permissions when needed',
      'Improve consent screen messaging in app registration',
      'Educate users about the application and its requirements',
      'Consider admin consent for tenant-wide deployment',
    ],
    relatedCodes: ['consent_required', 'AADSTS65001'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/consent-framework',
    whoFixes: 'both',
    iamActions: [
      'Review if admin consent can be granted tenant-wide',
      'Verify requested permissions are appropriate',
      'Consider customizing admin consent request text',
    ],
    developerActions: [
      'Implement proper error messaging when consent is declined',
      'Allow user to retry consent flow',
      'Request only minimum necessary permissions',
      'Use incremental consent pattern',
    ],
    estimatedFixTime: '10-15 minutes',
  },
  'AADSTS700051': {
    code: 'AADSTS700051',
    category: 'configuration',
    severity: 'error',
    description: 'The response_type parameter is not enabled for the application.',
    commonCauses: [
      'Using authorization code flow but not enabled in app registration',
      'Implicit flow requested but not allowed',
      'Platform type misconfigured (Web vs SPA vs Mobile)',
      'OAuth2 flow settings not properly configured',
    ],
    solutions: [
      'Enable appropriate authentication flows in app registration (Authentication blade)',
      'For SPAs: Enable "Access tokens" and "ID tokens" under Implicit grant',
      'For web apps: Ensure authorization code flow is enabled',
      'Verify platform configuration matches your application type',
      'Check "Supported account types" setting',
    ],
    codeExample: `// Ensure your MSAL config matches app registration
const msalConfig = {
  auth: {
    clientId: 'your-client-id',
    authority: 'https://login.microsoftonline.com/your-tenant-id',
    redirectUri: 'https://your-app.com/callback',
  },
  // For SPA, use 'popup' or 'redirect' interaction type
};`,
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-auth-code-flow',
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → App Registrations → [Your App] → Authentication',
      'Under "Implicit grant and hybrid flows", enable "Access tokens" and "ID tokens"',
      'Or add appropriate platform (Single-page application, Web, Mobile)',
      'Click Save',
    ],
    estimatedFixTime: '3 minutes',
  },
  'AADSTS90014': {
    code: 'AADSTS90014',
    category: 'configuration',
    severity: 'error',
    description: 'A required field is missing from the credential. This could be caused by missing authorization code or refresh token.',
    commonCauses: [
      'Missing required parameter in authentication request',
      'Authorization code not included in token request',
      'Refresh token missing when attempting to refresh',
      'Client secret missing for confidential client',
      'Scope parameter missing or empty',
    ],
    solutions: [
      'Verify all required OAuth2 parameters are included',
      'Ensure authorization code is properly extracted from callback',
      'Check that refresh tokens are being stored and retrieved correctly',
      'For confidential clients, provide client secret or certificate',
      'Always include scope parameter in token requests',
      'Review OAuth2 flow implementation for missing steps',
    ],
    codeExample: `// Ensure all required parameters are present
const tokenRequest = {
  scopes: ['User.Read'],
  account: account, // Required: the account to acquire token for
  // For auth code flow:
  code: authCode, // The authorization code from redirect
};`,
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-auth-code-flow',
    whoFixes: 'developer',
    developerActions: [
      'Review OAuth2 flow implementation',
      'Ensure all required parameters are included in requests',
      'Check authorization code extraction from callback',
      'Verify scope parameter is always provided',
    ],
    estimatedFixTime: '20-30 minutes',
  },
  'invalid_client': {
    code: 'invalid_client',
    category: 'configuration',
    severity: 'error',
    description: 'Client authentication failed. Typically due to incorrect client secret or certificate.',
    commonCauses: [
      'Client secret expired or incorrect',
      'Client certificate expired or invalid',
      'Wrong client ID/secret combination',
      'Confidential client misconfigured',
    ],
    solutions: [
      'Generate new client secret in Azure portal',
      'Verify client secret matches app registration',
      'Check certificate validity and thumbprint',
      'Ensure client credentials are properly stored',
      'Use Key Vault for secret management',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-authentication-flows#client-credentials',
    whoFixes: 'both',
    iamActions: [
      'Check current secret expiration: App Registrations → Certificates & secrets',
      'Generate new client secret if expired',
      'Securely share new secret with developer team',
    ],
    developerActions: [
      'Verify clientSecret in configuration matches Azure Portal value',
      'Update secret in application configuration or Key Vault',
      'For certificates: verify thumbprint and ensure certificate is not expired',
    ],
    estimatedFixTime: '5-10 minutes',
  },
  'network_error': {
    code: 'network_error',
    category: 'network',
    severity: 'error',
    description: 'Network request failed. Unable to reach authentication endpoint.',
    commonCauses: [
      'No internet connectivity',
      'Firewall blocking Azure AD endpoints',
      'Proxy configuration issues',
      'CORS errors in browser',
      'DNS resolution failures',
    ],
    solutions: [
      'Check internet connectivity',
      'Verify firewall allows access to login.microsoftonline.com',
      'Configure proxy settings if needed',
      'Whitelist Azure AD endpoints',
      'Check browser console for CORS errors',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-known-issues',
    whoFixes: 'user',
    estimatedFixTime: '5-30 minutes',
  },
  'monitor_window_timeout': {
    code: 'monitor_window_timeout',
    category: 'configuration',
    severity: 'warning',
    description: 'Popup window timed out waiting for response from authentication server.',
    commonCauses: [
      'User closed popup window before completing auth',
      'Popup blocked by browser',
      'Slow network response',
      'Authentication timeout too short',
    ],
    solutions: [
      'Increase timeout configuration',
      'Use redirect flow instead of popup',
      'Check for popup blockers',
      'Inform users not to close popup window',
      'Implement proper timeout handling',
    ],
    codeExample: `const msalConfig = {
  system: {
    windowHashTimeout: 60000, // Increase timeout
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0
  }
};`,
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications',
    whoFixes: 'developer',
    developerActions: [
      'Increase timeout in MSAL system configuration',
      'Consider using redirect flow instead of popup',
      'Add user guidance to wait for authentication to complete',
      'Implement retry logic for timeout errors',
    ],
    estimatedFixTime: '15 minutes',
  },
  'AADSTS50053': {
    code: 'AADSTS50053',
    category: 'authentication',
    severity: 'error',
    description: 'Account is locked because the user tried to sign in too many times with incorrect credentials.',
    commonCauses: [
      'Smart lockout triggered due to failed sign-in attempts',
      'Brute force protection activated',
      'Multiple incorrect password attempts',
    ],
    solutions: [
      'Wait for lockout period to expire (typically 1 minute)',
      'User should reset password',
      'Admin can unlock account in Azure portal',
      'Review sign-in logs for suspicious activity',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/authentication/howto-password-smart-lockout',
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → Azure AD → Users → [User]',
      'Check sign-in logs for lockout events',
      'Unlock account if necessary (wait 60 seconds or manually unlock)',
      'Review for suspicious activity patterns',
      'Consider password reset if account compromised',
    ],
    estimatedFixTime: '5 minutes (plus wait time)',
  },
  'AADSTS650053': {
    code: 'AADSTS650053',
    category: 'configuration',
    severity: 'error',
    description: 'The application asked for scope that doesn\'t exist on the resource. The requested scope is not valid or not available for the target resource.',
    commonCauses: [
      'Application requesting a scope that doesn\'t exist on the target resource',
      'Typo in the scope name requested by the application',
      'Scope was removed or renamed in the resource API',
      'Application configuration uses incorrect scope format',
      'Resource API has not exposed the requested scope/permission',
    ],
    solutions: [
      'Verify the requested scope exists on the target resource API',
      'Check for typos in scope names in your code',
      'Review API Permissions in app registration to ensure correct scopes',
      'Consult the resource API documentation for valid scopes',
      'Update application code to use correct scope names',
      'If you control the resource API, ensure the scope is properly exposed',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/scopes-oidc',
    whoFixes: 'developer',
    developerActions: [
      'Check the scope parameter in your token request',
      'Verify scope format: api://{client-id}/{scope-name} or {resource-uri}/{scope-name}',
      'Review Azure Portal → App Registrations → API Permissions for valid scopes',
      'Update code to use correct scope values',
      'If custom API: Check App Registrations → Expose an API → Scopes',
    ],
    estimatedFixTime: '10-20 minutes',
  },
  'AADSTS50126': {
    code: 'AADSTS50126',
    category: 'authentication',
    severity: 'error',
    description: 'Invalid username or password. Error validating credentials due to invalid username or password.',
    commonCauses: [
      'User entered incorrect username or password',
      'Password has expired and needs to be changed',
      'User account is locked out',
      'Caps Lock is on causing password mismatch',
    ],
    solutions: [
      'Verify username and password are correct',
      'Check if password has expired and needs reset',
      'Use self-service password reset portal',
      'Contact administrator if account is locked',
      'Ensure correct domain/tenant is selected',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/authentication/concept-sspr-howitworks',
    whoFixes: 'user',
    estimatedFixTime: '2-10 minutes',
  },
  'AADSTS50034': {
    code: 'AADSTS50034',
    category: 'configuration',
    severity: 'error',
    description: 'User account does not exist in the directory. The user must be added as an external user or invited.',
    commonCauses: [
      'User does not exist in the tenant',
      'User email address is incorrect or misspelled',
      'Guest user has not been invited',
      'User account has been deleted',
    ],
    solutions: [
      'Verify the correct user principal name (UPN)',
      'Invite user as guest if external collaboration needed',
      'Add user to the Azure AD tenant',
      'Check for typos in email address',
      'Ensure user exists in correct tenant',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/external-identities/what-is-b2b',
    whoFixes: 'iam',
    iamActions: [
      'Verify user email address is correct',
      'Navigate to Azure Portal → Azure AD → Users',
      'Search for user to confirm if they exist',
      'If external user: Click "New guest user" and send invitation',
      'If internal user: Create new user account',
    ],
    estimatedFixTime: '5-10 minutes',
  },
  'AADSTS7000215': {
    code: 'AADSTS7000215',
    category: 'configuration',
    severity: 'error',
    description: 'Invalid client secret or credential is expired. The confidential client application requires a valid credential.',
    commonCauses: [
      'Client secret has expired',
      'Client secret value is incorrect',
      'Certificate has expired or is invalid',
      'Wrong client secret being used',
    ],
    solutions: [
      'Generate new client secret in app registration',
      'Update application configuration with new secret',
      'Verify client secret value matches Azure portal',
      'Check certificate expiration date and renew if needed',
      'Use Azure Key Vault to manage secrets securely',
      'Set up monitoring for expiring secrets',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/howto-create-service-principal-portal',
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → App Registrations → [Your App] → Certificates & secrets',
      'Click "New client secret"',
      'Set description and expiration period',
      'Copy the secret VALUE (not the Secret ID) immediately',
      'Securely provide the new secret to the developer team',
    ],
    estimatedFixTime: '5 minutes',
  },
  'AADSTS50079': {
    code: 'AADSTS50079',
    category: 'authentication',
    severity: 'warning',
    description: 'User needs to enroll for multi-factor authentication (MFA) due to security policy requirements.',
    commonCauses: [
      'MFA enrollment is required but not completed',
      'Security policy requires MFA but user has not set it up',
      'User needs to register authentication methods',
    ],
    solutions: [
      'Direct user to https://aka.ms/mfasetup to enroll',
      'Guide user through MFA registration process',
      'Ensure user has access to phone or authenticator app',
      'Review security policies and inform users in advance',
      'Provide documentation for MFA enrollment',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/authentication/concept-mfa-howitworks',
    whoFixes: 'user',
    estimatedFixTime: '5-10 minutes',
  },
  'AADSTS54005': {
    code: 'AADSTS54005',
    category: 'configuration',
    severity: 'error',
    description: 'OAuth2 Authorization code was already redeemed. Authorization codes can only be used once.',
    commonCauses: [
      'Authorization code was used more than once',
      'Race condition in token acquisition',
      'Replay attack attempt',
      'Double submission of authorization code',
    ],
    solutions: [
      'Implement idempotency in token acquisition logic',
      'Ensure code is only exchanged once',
      'Check for duplicate requests in application',
      'Review application flow to prevent code reuse',
      'Monitor for potential security issues',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-auth-code-flow',
    whoFixes: 'developer',
    developerActions: [
      'Ensure authorization code is only used once',
      'Check for race conditions in token acquisition',
      'Implement proper state management to prevent duplicate requests',
      'Review OAuth flow for code reuse issues',
    ],
    estimatedFixTime: '30-45 minutes',
  },
  'popup_window_error': {
    code: 'popup_window_error',
    category: 'configuration',
    severity: 'warning',
    description: 'Error occurred in popup window during authentication.',
    commonCauses: [
      'Popup window was blocked by browser',
      'User closed popup before completing auth',
      'Pop-up blocker extension installed',
    ],
    solutions: [
      'Use redirect flow (loginRedirect) instead of popup',
      'Whitelist your domain in popup blocker',
      'Educate users to allow popups for your app',
      'Implement fallback to redirect flow',
    ],
    codeExample: `// Fallback pattern
try {
  await msalInstance.loginPopup(request);
} catch (error) {
  if (error.errorCode === 'popup_window_error') {
    // Fallback to redirect
    await msalInstance.loginRedirect(request);
  }
}`,
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-use-ie-browser',
    whoFixes: 'developer',
    developerActions: [
      'Implement fallback to redirect flow',
      'Add user guidance about allowing popups',
      'Consider using redirect flow as primary method',
      'Handle popup_window_error gracefully',
    ],
    estimatedFixTime: '15-20 minutes',
  },
  'AADSTS500011': {
    code: 'AADSTS500011',
    category: 'configuration',
    severity: 'error',
    description: 'The resource principal was not found in the tenant. This can happen if the application (API) has not been installed by the administrator of the tenant or consented to by any user in the tenant.',
    commonCauses: [
      'Custom API not registered as app registration in Azure AD',
      'API permissions not configured in client app registration',
      'Wrong scope format used (e.g., missing "api://" prefix)',
      'Typo in API client ID or scope name',
      'API app deleted or not yet created in target tenant',
    ],
    solutions: [
      'Verify API application exists in Azure AD app registrations',
      'Register the API as an app in Azure Portal if not already done',
      'Add API permissions in client app (API permissions blade)',
      'Verify scope format: api://{api-client-id}/{scope-name}',
      'Ensure API has "Expose an API" configured with scopes',
      'Check that client app has requested permissions to the API',
    ],
    relatedCodes: ['AADSTS65005', 'AADSTS650056'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes',
    codeExample: `// Correct scope format for custom API
const tokenRequest = {
  scopes: ["api://abc-123-api-client-id/Tasks.Read"],
  account: account
};

// WRONG formats that cause this error:
// scopes: ["Tasks.Read"]  // Missing api:// prefix
// scopes: ["https://myapi.com/Tasks.Read"]  // Wrong format
// scopes: ["api://wrong-id/Tasks.Read"]  // Wrong API client ID`,
    whoFixes: 'both',
    iamActions: [
      'Verify API app registration exists: Azure Portal → App Registrations',
      'Navigate to API app → Expose an API → ensure scopes are defined',
      'Navigate to client app → API permissions → Add permission → My APIs',
      'Select your API and grant the required delegated permissions',
      'Provide correct API client ID and scope names to developer',
    ],
    developerActions: [
      'Update scope format to: api://{api-client-id}/{scope-name}',
      'Verify API client ID matches Azure Portal',
      'Check scope names match what API exposes',
      'Request IAM to verify API app registration exists',
    ],
    estimatedFixTime: '5-10 minutes',
  },
  'AADSTS700084': {
    code: 'AADSTS700084',
    category: 'authentication',
    severity: 'error',
    description: 'The refresh token was issued to a single page app (SPA), and therefore has a fixed, limited lifetime of 24 hours, which cannot be extended. It is now expired and a new sign in request must be sent by the SPA to the sign in page.',
    commonCauses: [
      'User has not used the SPA for more than 24 hours',
      'Refresh token reached its 24-hour maximum lifetime for SPAs',
      'SPA attempting to refresh token after 24-hour window',
      'App running in background without user activity for extended period',
    ],
    solutions: [
      'Prompt user to sign in again interactively',
      'Clear the token cache and reinitialize authentication',
      'Implement proper session timeout warnings (e.g., at 23 hours)',
      'Educate users that SPAs require re-login after 24 hours of inactivity',
      'Consider using confidential client (backend) if 24h limit is too restrictive',
      'Handle this error gracefully with user-friendly message',
    ],
    relatedCodes: ['invalid_grant', 'AADSTS700082'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/refresh-tokens',
    codeExample: `try {
  const response = await msalInstance.acquireTokenSilent(request);
} catch (error) {
  if (error.errorCode === 'AADSTS700084') {
    // SPA refresh token expired (24h limit)
    // Clear cache and prompt re-login
    await msalInstance.clearCache();
    await msalInstance.loginRedirect(request);
  }
}`,
    whoFixes: 'developer',
    developerActions: [
      'Implement proper error handling for 24-hour expiration',
      'Add session timeout warnings before expiration',
      'Clear MSAL cache and trigger interactive login',
      'Display user-friendly message: "Session expired, please sign in again"',
      'This is expected behavior for SPAs - not a bug to fix',
    ],
    estimatedFixTime: '15-20 minutes',
  },
  'AADSTS65005': {
    code: 'AADSTS65005',
    category: 'configuration',
    severity: 'error',
    description: 'The app required resource access list does not contain apps discoverable by the resource, or the client app has requested access to resource which was not specified in its required resource access list.',
    commonCauses: [
      'API permissions not configured in app registration',
      'Requesting scope that is not in API permissions list',
      'Wrong scope format or typo in scope name',
      'API application identifier (URI) misconfigured',
      'Permissions configured but not granted (consent not given)',
    ],
    solutions: [
      'Add required API permissions in Azure Portal (API permissions blade)',
      'Verify scope names exactly match what is configured in API permissions',
      'Grant admin consent for API permissions',
      'Ensure API "Application ID URI" matches scope prefix',
      'For SAML apps: verify Identifier (Entity ID) is configured correctly',
      'Check that requested permissions are exposed by the API',
    ],
    relatedCodes: ['AADSTS500011', 'AADSTS650056', 'AADSTS65001'],
    docsUrl: 'https://learn.microsoft.com/en-us/troubleshoot/azure/active-directory/error-code-aadsts650056-misconfigured-app',
    codeExample: `// Ensure code requests scopes that are configured in portal
const tokenRequest = {
  scopes: [
    "api://abc-123/Tasks.Read",  // Must exist in API permissions
    "User.Read"                   // Must be granted in permissions
  ]
};

// Check Azure Portal:
// App Registration → API permissions
// Verify all requested scopes appear in the list`,
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → App Registrations → [Client App]',
      'Click API permissions → Add a permission',
      'Select the API (My APIs or Microsoft Graph)',
      'Select the required delegated permissions',
      'Click "Grant admin consent for [tenant]"',
      'Verify Status shows "Granted for [tenant]" with green checkmark',
    ],
    estimatedFixTime: '3-5 minutes',
  },
  'AADSTS50173': {
    code: 'AADSTS50173',
    category: 'authentication',
    severity: 'error',
    description: 'The provided grant has expired due to it being revoked. A fresh auth token is needed. The user might have changed or reset their password.',
    commonCauses: [
      'User changed their password',
      'User reset their password via self-service',
      'Administrator reset user password',
      'Refresh token was revoked by administrator',
      'User credentials were revoked due to security concerns',
      'Time gap between grant issuance and token validation is too large',
    ],
    solutions: [
      'Prompt user to sign in again with new credentials',
      'Clear token cache to remove stale tokens',
      'Implement interactive authentication flow',
      'Check if user needs to reset their password',
      'Verify user account is active and not disabled',
      'Handle this error gracefully with clear user message',
    ],
    relatedCodes: ['invalid_grant', 'AADSTS50055', 'AADSTS50126'],
    docsUrl: 'https://learn.microsoft.com/en-us/troubleshoot/entra/entra-id/app-integration/error-code-aadsts50173-grant-expired-revoked',
    codeExample: `try {
  const response = await msalInstance.acquireTokenSilent(request);
} catch (error) {
  if (error.errorCode === 'AADSTS50173') {
    // Token revoked - user likely changed password
    // Clear cache and require fresh login
    await msalInstance.clearCache();
    await msalInstance.loginPopup({
      ...request,
      prompt: 'login' // Force fresh login
    });
  }
}`,
    whoFixes: 'developer',
    developerActions: [
      'Implement error handling for revoked grants',
      'Clear MSAL token cache completely',
      'Trigger interactive login with prompt: "login"',
      'Display message: "Your credentials have changed, please sign in again"',
      'Do not retry silently - user interaction is required',
    ],
    estimatedFixTime: '10-15 minutes',
  },
  'AADSTS90094': {
    code: 'AADSTS90094',
    category: 'consent',
    severity: 'error',
    description: 'Administrator consent is required for the requested permissions. The application requires admin consent to access resources.',
    commonCauses: [
      'Application requires admin-only permissions (e.g., Directory.Read.All)',
      'Organization policy requires admin consent for all apps',
      'Application permission (not delegated) requires admin approval',
      'User trying to consent to app but admin approval is needed',
      'Tenant settings require admin to pre-approve all applications',
    ],
    solutions: [
      'Admin must grant tenant-wide consent in Azure portal',
      'Use admin consent URL to prompt admin for approval',
      'Navigate to Enterprise Applications → Permissions → Grant admin consent',
      'Review if admin-only permissions are truly needed',
      'Consider requesting only delegated (user) permissions if appropriate',
      'Contact tenant administrator to grant consent',
    ],
    relatedCodes: ['AADSTS65001', 'consent_required', 'AADSTS65004'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/develop/v2-admin-consent',
    codeExample: `// Build admin consent URL
const tenantId = 'your-tenant-id';
const clientId = 'your-client-id';
const redirectUri = 'https://yourapp.com/admin-callback';

const adminConsentUrl = 
  'https://login.microsoftonline.com/' + tenantId + 
  '/adminconsent' +
  '?client_id=' + clientId +
  '&redirect_uri=' + encodeURIComponent(redirectUri);

// Direct admin to this URL to grant consent
console.log('Admin consent URL:', adminConsentUrl);`,
    whoFixes: 'iam',
    iamActions: [
      'Navigate to Azure Portal → Enterprise Applications → [Your App]',
      'Click Permissions in left menu',
      'Click "Grant admin consent for [tenant name]" button',
      'Review all requested permissions carefully',
      'Click Accept to grant tenant-wide consent',
      'Alternative: Use admin consent URL provided by developer',
    ],
    estimatedFixTime: '2-3 minutes',
  },
  'AADSTS50074': {
    code: 'AADSTS50074',
    category: 'authentication',
    severity: 'warning',
    description: 'Strong authentication is required and the user did not pass the MFA challenge. Multi-factor authentication is needed to access the resource.',
    commonCauses: [
      'Conditional Access policy requires MFA but user has not completed it',
      'User denied MFA prompt or closed MFA challenge',
      'User does not have MFA methods registered',
      'MFA authentication attempt failed or timed out',
      'User cancelled MFA authentication',
    ],
    solutions: [
      'Retry authentication and complete MFA challenge',
      'Ensure user has MFA methods registered (phone, app, etc.)',
      'Complete MFA enrollment at https://aka.ms/mfasetup',
      'Verify Conditional Access policy MFA requirements',
      'Check if user account has MFA properly configured',
      'Use interactive authentication to present MFA challenge',
    ],
    relatedCodes: ['AADSTS50076', 'AADSTS50079', 'interaction_required'],
    docsUrl: 'https://learn.microsoft.com/en-us/azure/active-directory/authentication/concept-mfa-howitworks',
    codeExample: `try {
  const response = await msalInstance.acquireTokenSilent(request);
} catch (error) {
  if (error.errorCode === 'AADSTS50074') {
    // MFA required but not completed
    // Trigger interactive login to present MFA challenge
    await msalInstance.acquireTokenPopup({
      ...request,
      prompt: 'select_account' // Allow user to complete MFA
    });
  }
}`,
    whoFixes: 'both',
    iamActions: [
      'Verify user has MFA methods registered',
      'Check Conditional Access policies requiring MFA',
      'Guide user to enroll MFA: https://aka.ms/mfasetup',
      'Review if MFA requirement is appropriate for this app',
    ],
    developerActions: [
      'Implement proper handling of MFA requirement',
      'Trigger interactive authentication when this error occurs',
      'Allow user to complete MFA challenge',
      'Display user-friendly message about MFA requirement',
      'This is expected behavior when CA requires MFA',
    ],
    estimatedFixTime: '5-10 minutes',
  },
};

const MsalErrorDecoder: React.FC<MsalErrorDecoderProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [matchedError, setMatchedError] = useState<ErrorDefinition | null>(null);
  const [copiedSolution, setCopiedSolution] = useState<number | null>(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('list');
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedSolution(index);
    setTimeout(() => setCopiedSolution(null), 2000);
  };

  const handleCloseError = () => {
    setMatchedError(null);
    setDialogOpen(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getWhoFixesInfo = (whoFixes: string) => {
    switch (whoFixes) {
      case 'iam':
        return {
          label: 'IAM Specialist',
          icon: <AdminPanelSettingsIcon />,
          color: '#1976d2',
          bgColor: 'rgba(25, 118, 210, 0.08)',
          description: 'This can be fixed in the Azure Portal by an IAM administrator',
        };
      case 'developer':
        return {
          label: 'Developer',
          icon: <CodeIcon />,
          color: '#f57c00',
          bgColor: 'rgba(245, 124, 0, 0.08)',
          description: 'This requires code changes by a developer',
        };
      case 'both':
        return {
          label: 'IAM + Developer',
          icon: <GroupIcon />,
          color: '#7b1fa2',
          bgColor: 'rgba(123, 31, 162, 0.08)',
          description: 'This requires coordination between IAM and development teams',
        };
      case 'user':
        return {
          label: 'End User',
          icon: <PersonIcon />,
          color: '#388e3c',
          bgColor: 'rgba(56, 142, 60, 0.08)',
          description: 'This should be resolved by the end user or their IT support',
        };
      default:
        return {
          label: 'Unknown',
          icon: <InfoIcon />,
          color: '#616161',
          bgColor: 'rgba(97, 97, 97, 0.08)',
          description: 'Contact your administrator',
        };
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'authentication':
        return '#1976d2';
      case 'configuration':
        return '#f57c00';
      case 'network':
        return '#d32f2f';
      case 'consent':
        return '#388e3c';
      case 'token':
        return '#7b1fa2';
      case 'server':
        return '#616161';
      default:
        return '#616161';
    }
  };

  // Filter errors based on search term
  const filteredErrors = searchTerm
    ? Object.values(MSAL_ERRORS).filter(
        (err) =>
          err.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          err.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          err.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : Object.values(MSAL_ERRORS);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderColor: 'primary.main',
                color: 'primary.main',
              },
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              MSAL Error Decoder
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Decode MSAL error codes and get actionable solutions for authentication issues
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Error Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseError}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {matchedError && (
          <>
            <DialogTitle sx={{ 
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BugReportIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Error Details
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={matchedError.category}
                  size="small"
                  sx={{
                    backgroundColor: getCategoryColor(matchedError.category),
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={getSeverityIcon(matchedError.severity)}
                  label={matchedError.severity.toUpperCase()}
                  size="small"
                  color={getSeverityColor(matchedError.severity) as any}
                />
                <Tooltip title="Close">
                  <IconButton
                    size="small"
                    onClick={handleCloseError}
                    sx={{ ml: 1 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3, maxHeight: '70vh' }}>
              {/* Error Code */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: 'rgba(211, 47, 47, 0.05)',
                  borderColor: 'error.light',
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Error Code:
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'error.main',
                    fontWeight: 600,
                  }}
                >
                  {matchedError.code}
                </Typography>
              </Paper>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Description:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {matchedError.description}
                </Typography>
              </Box>

              {/* Who Fixes This? - Prominent Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 3,
                  backgroundColor: getWhoFixesInfo(matchedError.whoFixes).bgColor,
                  borderColor: getWhoFixesInfo(matchedError.whoFixes).color,
                  borderWidth: 2,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: getWhoFixesInfo(matchedError.whoFixes).color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    {React.cloneElement(getWhoFixesInfo(matchedError.whoFixes).icon, {
                      sx: { color: 'white', fontSize: '1.5rem' },
                    })}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.25 }}>
                      WHO FIXES THIS?
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: getWhoFixesInfo(matchedError.whoFixes).color }}>
                      {getWhoFixesInfo(matchedError.whoFixes).label}
                    </Typography>
                  </Box>
                  {matchedError.estimatedFixTime && (
                    <Chip
                      icon={<TimerIcon />}
                      label={matchedError.estimatedFixTime}
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        fontWeight: 600,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: getWhoFixesInfo(matchedError.whoFixes).color,
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {getWhoFixesInfo(matchedError.whoFixes).description}
                </Typography>

                {/* IAM Actions */}
                {matchedError.iamActions && matchedError.iamActions.length > 0 && (
                  <Box sx={{ mb: matchedError.developerActions ? 2 : 0 }}>
                    <Alert
                      severity="info"
                      icon={<AdminPanelSettingsIcon />}
                      sx={{
                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                        '& .MuiAlert-icon': {
                          color: '#1976d2',
                        },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        IAM Specialist Actions:
                      </Typography>
                      <List dense sx={{ pl: 0 }}>
                        {matchedError.iamActions.map((action, index) => (
                          <ListItem key={index} sx={{ pl: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                {index + 1}.
                              </Typography>
                            </ListItemIcon>
                            <ListItemText
                              primary={action}
                              primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.875rem' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  </Box>
                )}

                {/* Developer Actions */}
                {matchedError.developerActions && matchedError.developerActions.length > 0 && (
                  <Box>
                    <Alert
                      severity="warning"
                      icon={<CodeIcon />}
                      sx={{
                        backgroundColor: 'rgba(245, 124, 0, 0.05)',
                        '& .MuiAlert-icon': {
                          color: '#f57c00',
                        },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Developer Actions:
                      </Typography>
                      <List dense sx={{ pl: 0 }}>
                        {matchedError.developerActions.map((action, index) => (
                          <ListItem key={index} sx={{ pl: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00' }}>
                                {index + 1}.
                              </Typography>
                            </ListItemIcon>
                            <ListItemText
                              primary={action}
                              primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.875rem' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  </Box>
                )}
              </Paper>

              {/* Common Causes */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <WarningIcon sx={{ mr: 1, color: 'warning.main', fontSize: '1.2rem' }} />
                  Common Causes:
                </Typography>
                <List dense>
                  {matchedError.commonCauses.map((cause, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ErrorIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={cause}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Solutions */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <BuildIcon sx={{ mr: 1, color: 'success.main', fontSize: '1.2rem' }} />
                  Solutions:
                </Typography>
                <List dense>
                  {matchedError.solutions.map((solution, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.05)',
                        borderRadius: 1,
                        mb: 1,
                      }}
                      secondaryAction={
                        <Tooltip title={copiedSolution === index ? 'Copied!' : 'Copy solution'}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleCopy(solution, index)}
                          >
                            {copiedSolution === index ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={solution}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Code Example */}
              {matchedError.codeExample && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                    <CodeIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                    Code Example:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 2,
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {matchedError.codeExample}
                    </pre>
                  </Paper>
                </Box>
              )}

              {/* Related Errors */}
              {matchedError.relatedCodes && matchedError.relatedCodes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Related Error Codes:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {matchedError.relatedCodes.map((code) => (
                      <Chip
                        key={code}
                        label={code}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          const relatedError = MSAL_ERRORS[code];
                          if (relatedError) {
                            setMatchedError(relatedError);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Documentation Link */}
              {matchedError.docsUrl && (
                <Alert severity="info" icon={<LinkIcon />} sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Learn More:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href={matchedError.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {matchedError.docsUrl}
                  </Typography>
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
              <Button onClick={handleCloseError} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Common Errors Reference */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              backgroundColor: '#f9f9f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Common MSAL Errors Reference
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Filter errors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: 250 }}
                InputProps={{
                  endAdornment: searchTerm && (
                    <IconButton 
                      size="small" 
                      onClick={() => setSearchTerm('')}
                      sx={{ p: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="cards">
                  <Tooltip title="Card view">
                    <ViewModuleIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="List view">
                    <ViewListIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {viewMode === 'cards' ? (
              <Grid container spacing={2}>
                {filteredErrors.map((error) => (
                  <Grid item xs={12} sm={6} md={4} key={error.code}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: 'primary.main',
                        },
                      }}
                      onClick={() => {
                        setMatchedError(error);
                        setDialogOpen(true);
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: 'error.main',
                          mb: 1,
                          fontSize: '1rem',
                        }}
                      >
                        {error.code}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip
                            label={error.category}
                            size="small"
                            sx={{
                              backgroundColor: getCategoryColor(error.category),
                              color: 'white',
                              fontSize: '0.7rem',
                            }}
                          />
                          <Chip
                            icon={getSeverityIcon(error.severity)}
                            label={error.severity}
                            size="small"
                            color={getSeverityColor(error.severity) as any}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Chip
                          icon={React.cloneElement(getWhoFixesInfo(error.whoFixes).icon, { sx: { fontSize: '0.85rem' } })}
                          label={getWhoFixesInfo(error.whoFixes).label}
                          size="small"
                          sx={{
                            backgroundColor: getWhoFixesInfo(error.whoFixes).color,
                            color: 'white',
                            fontSize: '0.65rem',
                            height: 20,
                            fontWeight: 600,
                            '& .MuiChip-icon': {
                              color: 'white',
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {error.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <List sx={{ width: '100%' }}>
                {filteredErrors.map((error, index) => (
                  <React.Fragment key={error.code}>
                    <ListItemButton
                      onClick={() => {
                        setMatchedError(error);
                        setDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getSeverityIcon(error.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: 'error.main',
                              }}
                            >
                              {error.code}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Chip
                                label={error.category}
                                size="small"
                                sx={{
                                  backgroundColor: getCategoryColor(error.category),
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 20,
                                }}
                              />
                              <Chip
                                label={error.severity}
                                size="small"
                                color={getSeverityColor(error.severity) as any}
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                              <Chip
                                icon={React.cloneElement(getWhoFixesInfo(error.whoFixes).icon, { 
                                  sx: { fontSize: '0.75rem !important' } 
                                })}
                                label={getWhoFixesInfo(error.whoFixes).label}
                                size="small"
                                sx={{
                                  backgroundColor: getWhoFixesInfo(error.whoFixes).color,
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 20,
                                  fontWeight: 600,
                                  '& .MuiChip-icon': {
                                    color: 'white',
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {error.description}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    {index < filteredErrors.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* About Section - Expandable */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Accordion expanded={aboutExpanded} onChange={() => setAboutExpanded(!aboutExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About MSAL Error Decoder
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                The MSAL Error Decoder helps you quickly understand and resolve Microsoft Authentication Library (MSAL)
                errors. It provides detailed explanations, common causes, and actionable solutions for authentication
                issues you may encounter in your applications.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  What is MSAL?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  MSAL (Microsoft Authentication Library) is a library that enables developers to acquire tokens from
                  the Microsoft identity platform to authenticate users and access secured web APIs. It supports various
                  authentication flows and platforms including JavaScript, React, Angular, Node.js, .NET, and more.
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  How to Use This Tool:
                </Typography>
                <Box component="ol" sx={{ pl: 3, '& li': { mb: 1 } }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Enter the error code</strong> you encountered (e.g., "invalid_grant", "AADSTS50058")
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Click "Decode Error"</strong> to see detailed information about the error
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Review common causes</strong> to understand why the error occurred
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Follow the solutions</strong> provided to fix the issue
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Browse the reference</strong> section below for all available error codes
                    </Typography>
                  </li>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Error Categories:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(25, 118, 210, 0.05)' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                        Authentication
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User sign-in, session management, and credential issues
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(245, 124, 0, 0.05)' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00', mb: 0.5 }}>
                        Configuration
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        App registration, client setup, and misconfiguration errors
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(56, 142, 60, 0.05)' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#388e3c', mb: 0.5 }}>
                        Consent
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Permission grants, admin consent, and scope-related issues
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(211, 47, 47, 0.05)' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5 }}>
                        Network
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Connectivity issues, timeouts, and endpoint access problems
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Additional Resources:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LinkIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          component="a"
                          href="https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-error-handling-js"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          MSAL.js Error Handling Documentation
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LinkIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          component="a"
                          href="https://learn.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          Azure AD Authentication Error Codes
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LinkIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          component="a"
                          href="https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-overview"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          MSAL Overview and Getting Started
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Box>
  );
};

export default MsalErrorDecoder;
